import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Plus, Loader2, FolderOpen, Calendar as CalIcon, Users, ShieldCheck, Megaphone, Check, X, Archive, Send, ExternalLink, EyeOff } from "lucide-react";
import { toast } from "sonner";

function HearingIntakesReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hearing-intakes");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/hearing-intakes/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUpdating(null); }
  };

  const STATUS_COLOR = {
    pending_review: "border-amber-400/40 text-amber-400",
    in_progress: "border-blue-500/40 text-blue-300",
    completed: "border-green-500/40 text-green-400",
    archived: "border-zinc-700 text-zinc-500",
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400 mx-auto"/></div>;
  if (items.length === 0) {
    return <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-12 text-center text-sm text-zinc-400" data-testid="no-hearing-intakes">
      No hearing intakes yet.
    </div>;
  }
  return (
    <div className="space-y-4" data-testid="hearing-intakes-list">
      {items.map((h) => {
        const d = h.data;
        return (
          <div key={h.id} className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={`hearing-intake-${h.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`text-[10px] font-mono tracking-[0.18em] uppercase border rounded-sm px-2 py-0.5 ${STATUS_COLOR[h.status]}`}>{h.status.replace("_", " ")}</span>
                  <Badge variant="outline" className="text-xs border-amber-400/30 text-amber-400 rounded-sm">{d.hearing_type}</Badge>
                  {d.hearing_date && <span className="text-[11px] text-zinc-400">{d.hearing_date}</span>}
                  <span className="text-[11px] text-zinc-500">submitted {new Date(h.created_at).toLocaleDateString()}</span>
                </div>
                <h3 className="display-serif text-xl text-zinc-50 leading-tight">{d.full_name}</h3>
                <p className="text-xs text-zinc-500 mt-1">{d.email}{d.phone ? ` · ${d.phone}` : ""}{d.din ? ` · DIN ${d.din}` : ""}{d.facility ? ` · ${d.facility}` : ""}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                {h.status === "pending_review" && (
                  <Button size="sm" onClick={() => setStatus(h.id, "in_progress")} disabled={updating === h.id} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white h-8">Start review</Button>
                )}
                {h.status === "in_progress" && (
                  <Button size="sm" onClick={() => setStatus(h.id, "completed")} disabled={updating === h.id} className="rounded-sm bg-green-700 hover:bg-green-600 text-white h-8"><Check className="h-3.5 w-3.5 mr-1"/>Complete</Button>
                )}
                <Button size="sm" onClick={() => setStatus(h.id, "archived")} disabled={updating === h.id} variant="ghost" className="rounded-sm text-zinc-400 h-8"><Archive className="h-3.5 w-3.5"/></Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm border-t border-zinc-800 pt-3">
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Case Summary</p><p className="text-zinc-200 whitespace-pre-wrap">{d.case_summary}</p></div>
              {d.goals && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Goals</p><p className="text-zinc-200 whitespace-pre-wrap">{d.goals}</p></div>}
              {d.key_witnesses && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Witnesses</p><p className="text-zinc-200 whitespace-pre-wrap">{d.key_witnesses}</p></div>}
              {d.documents_available && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Documents</p><p className="text-zinc-200 whitespace-pre-wrap">{d.documents_available}</p></div>}
              {d.location && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Location</p><p className="text-zinc-200">{d.location}</p></div>}
              {d.additional_notes && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Notes</p><p className="text-zinc-200 whitespace-pre-wrap">{d.additional_notes}</p></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PetitionsReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/petitions");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    setUpdating(id);
    try {
      await api.patch(`/admin/petitions/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUpdating(null); }
  };

  const publish = async (id) => {
    setUpdating(id);
    try {
      const { data } = await api.post(`/admin/petitions/${id}/publish`);
      const url = `${window.location.origin}/petitions/${data.slug}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(data.already_published ? `Already live — link copied: ${url}` : `Published! Link copied: ${url}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUpdating(null); }
  };

  const unpublish = async (id) => {
    setUpdating(id);
    try {
      await api.post(`/admin/petitions/${id}/unpublish`);
      toast.success("Petition unpublished");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUpdating(null); }
  };

  const STATUS_COLOR = {
    pending_review: "border-amber-400/40 text-amber-400",
    approved: "border-green-500/40 text-green-400",
    rejected: "border-red-500/40 text-red-400",
    archived: "border-zinc-700 text-zinc-500",
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400 mx-auto"/></div>;
  if (items.length === 0) {
    return <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-12 text-center text-sm text-zinc-400" data-testid="no-petitions">
      <Megaphone className="h-8 w-8 text-zinc-700 mx-auto mb-3" strokeWidth={1.5}/>
      No petition submissions yet.
    </div>;
  }

  return (
    <div className="space-y-4" data-testid="petitions-list">
      {items.map((p) => {
        const d = p.data;
        const isApproved = p.status === "approved";
        const isPending = p.status === "pending_review";
        return (
          <div key={p.id} className="bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid={`petition-${p.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`text-[10px] font-mono tracking-[0.18em] uppercase border rounded-sm px-2 py-0.5 ${STATUS_COLOR[p.status]}`}>{p.status.replace("_", " ")}</span>
                  {p.published && <span className="text-[10px] font-mono tracking-[0.18em] uppercase border rounded-sm px-2 py-0.5 border-blue-500/50 text-blue-300">PUBLISHED</span>}
                  <Badge variant="outline" className="text-xs border-amber-400/30 text-amber-400 rounded-sm">{d.category}</Badge>
                  <span className="text-[11px] text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</span>
                  {p.published && (
                    <span className="text-[11px] text-amber-400 font-medium">{p.signature_count?.toLocaleString() || 0} signatures</span>
                  )}
                </div>
                <h3 className="display-serif text-xl text-zinc-50 leading-tight">{d.title}</h3>
                <p className="text-xs text-zinc-500 mt-1">From <span className="text-zinc-300">{d.requester_name}</span> · {d.requester_email}{d.requester_phone ? ` · ${d.requester_phone}` : ""}{d.affiliation ? ` · ${d.affiliation}` : ""}</p>
                {p.published && p.slug && (
                  <Link to={`/petitions/${p.slug}`} target="_blank" className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-amber-400 mt-2 transition-colors" data-testid={`petition-view-${p.id}`}>
                    /petitions/{p.slug} <ExternalLink className="h-3 w-3"/>
                  </Link>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0 flex-wrap">
                {isPending && (
                  <>
                    <Button size="sm" onClick={() => update(p.id, "approved")} disabled={updating === p.id} className="rounded-sm bg-green-700 hover:bg-green-600 text-white h-8" data-testid={`petition-approve-${p.id}`}><Check className="h-3.5 w-3.5"/></Button>
                    <Button size="sm" onClick={() => update(p.id, "rejected")} disabled={updating === p.id} variant="outline" className="rounded-sm border-red-500/40 text-red-400 hover:bg-red-950/30 h-8" data-testid={`petition-reject-${p.id}`}><X className="h-3.5 w-3.5"/></Button>
                  </>
                )}
                {isApproved && !p.published && (
                  <Button size="sm" onClick={() => publish(p.id)} disabled={updating === p.id} className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white h-8" data-testid={`petition-publish-${p.id}`}>
                    {updating === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <><Send className="h-3.5 w-3.5 mr-1.5"/>Publish</>}
                  </Button>
                )}
                {p.published && (
                  <Button size="sm" onClick={() => unpublish(p.id)} disabled={updating === p.id} variant="outline" className="rounded-sm border-zinc-700 text-zinc-300 hover:border-amber-400 h-8" data-testid={`petition-unpublish-${p.id}`}>
                    <EyeOff className="h-3.5 w-3.5"/>
                  </Button>
                )}
                <Button size="sm" onClick={() => update(p.id, "archived")} disabled={updating === p.id} variant="ghost" className="rounded-sm text-zinc-400 h-8"><Archive className="h-3.5 w-3.5"/></Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm border-t border-zinc-800 pt-3">
              <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Cause</p><p className="text-zinc-200 whitespace-pre-wrap">{d.cause}</p></div>
              {d.impact && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Why it matters</p><p className="text-zinc-200 whitespace-pre-wrap">{d.impact}</p></div>}
              {d.proposed_action && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Proposed action</p><p className="text-zinc-200 whitespace-pre-wrap">{d.proposed_action}</p></div>}
              {d.target_audience && <div><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">Target</p><p className="text-zinc-200">{d.target_audience}</p></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreateClient({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", case_number: "", notes: "", status: "active" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.full_name) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Client created");
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", case_number: "", notes: "", status: "active" });
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-blue-700 hover:bg-blue-600" data-testid="new-client-btn">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New Client
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle>Add a new client</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Full name *</Label><Input value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} className="rounded-sm mt-1.5" data-testid="client-name-input"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-sm mt-1.5" data-testid="client-email-input"/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="rounded-sm mt-1.5" data-testid="client-phone-input"/></div>
          </div>
          <div><Label>Case number</Label><Input value={form.case_number} onChange={(e)=>setForm({...form, case_number: e.target.value})} className="rounded-sm mt-1.5" data-testid="client-case-input"/></div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="client-status-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="rounded-sm mt-1.5" data-testid="client-notes-input"/></div>
          <Button onClick={submit} disabled={saving} className="w-full rounded-sm bg-blue-700 hover:bg-blue-600" data-testid="save-client-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Save client
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "border-emerald-700/30 text-emerald-400 bg-emerald-950/40",
    pending: "border-amber-700/30 text-amber-400 bg-amber-950/40",
    closed: "border-zinc-700 text-zinc-300 bg-black",
  };
  return <Badge variant="outline" className={`rounded-sm text-xs ${map[status] || ""}`}>{status}</Badge>;
}

function ClientsTable({ loading, clients }) {
  if (loading) {
    return <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400 mx-auto"/></div>;
  }
  if (clients.length === 0) {
    return <div className="p-12 text-center text-sm text-zinc-400" data-testid="no-clients">No clients yet. Add your first client to get started.</div>;
  }
  return (
    <Table data-testid="clients-table">
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Case #</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {clients.map((c) => (
          <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
            <TableCell className="font-medium">{c.full_name}</TableCell>
            <TableCell className="font-mono text-xs">{c.case_number || "—"}</TableCell>
            <TableCell className="text-sm text-zinc-300">{c.email || c.phone || "—"}</TableCell>
            <TableCell><StatusBadge status={c.status}/></TableCell>
            <TableCell className="text-right">
              <Link to={`/admin/clients/${c.id}`} className="text-sm text-amber-400 hover:underline" data-testid={`view-client-${c.id}`}>Open →</Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function AdminDashboard() {
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, b] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/bookings"),
      ]);
      setClients(c.data);
      setBookings(b.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-black min-h-screen" data-testid="admin-dashboard">
      <div className="container-pl py-12">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5 text-amber-400" strokeWidth={1.5}/>
          <p className="overline">Admin Vault</p>
        </div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light text-zinc-50 mb-10">
          Client Management
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="stat-clients">
            <Users className="h-5 w-5 text-amber-400 mb-3" strokeWidth={1.5}/>
            <p className="text-3xl font-light text-zinc-50">{clients.length}</p>
            <p className="text-xs text-zinc-400 mt-1">Total Clients</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="stat-active">
            <FolderOpen className="h-5 w-5 text-amber-400 mb-3" strokeWidth={1.5}/>
            <p className="text-3xl font-light text-zinc-50">{clients.filter(c => c.status === "active").length}</p>
            <p className="text-xs text-zinc-400 mt-1">Active Cases</p>
          </div>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6" data-testid="stat-bookings">
            <CalIcon className="h-5 w-5 text-amber-400 mb-3" strokeWidth={1.5}/>
            <p className="text-3xl font-light text-zinc-50">{bookings.length}</p>
            <p className="text-xs text-zinc-400 mt-1">Bookings</p>
          </div>
        </div>

        <Tabs defaultValue="clients">
          <TabsList className="rounded-sm" data-testid="admin-tabs">
            <TabsTrigger value="clients" className="rounded-sm" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-sm" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="hearings" className="rounded-sm" data-testid="tab-hearings">Hearing Intakes</TabsTrigger>
            <TabsTrigger value="petitions" className="rounded-sm" data-testid="tab-petitions">Petitions</TabsTrigger>
          </TabsList>

          <TabsContent value="clients" className="mt-6">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
              <div className="flex justify-between items-center p-5 border-b border-zinc-800">
                <h2 className="font-medium text-zinc-50">All Clients</h2>
                <CreateClient onCreated={load}/>
              </div>
              <ClientsTable loading={loading} clients={clients} />
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-6">
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm">
              <div className="p-5 border-b border-zinc-800"><h2 className="font-medium text-zinc-50">Appointments</h2></div>
              {bookings.length === 0 ? (
                <div className="p-12 text-center text-sm text-zinc-400" data-testid="no-bookings">No bookings yet.</div>
              ) : (
                <Table data-testid="bookings-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Contact</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((b) => (
                      <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                        <TableCell className="font-medium">{b.name}</TableCell>
                        <TableCell className="capitalize">{b.appointment_type === "phone" ? "Phone" : "Consultation"}</TableCell>
                        <TableCell>{b.date}</TableCell>
                        <TableCell>{b.time_slot}</TableCell>
                        <TableCell className="text-sm text-zinc-300">{b.email}<br/><span className="text-xs">{b.phone}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="hearings" className="mt-6">
            <HearingIntakesReview/>
          </TabsContent>

          <TabsContent value="petitions" className="mt-6">
            <PetitionsReview/>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
